import { useState, useEffect } from 'react';

const useAdminMode = () => {
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  useEffect(() => {
    // Check if admin mode was previously enabled
    const savedAdminMode = localStorage.getItem('mba-admin-mode');
    if (savedAdminMode === 'true') {
      setIsAdminMode(true);
    }

    // Listen for the Konami code: ↑ ↑ ↓ ↓ ← → ← → B A
    const konamiCode = [
      'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
      'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
      'KeyB', 'KeyA'
    ];
    let konamiIndex = 0;

    const handleKeyDown = (event) => {
      if (event.code === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          toggleAdminMode();
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    // Listen for Ctrl+Shift+A and Ctrl+D (common debug shortcut)
    const handleKeyCombo = (event) => {
      if ((event.ctrlKey && event.shiftKey && event.key === 'A') ||
          (event.ctrlKey && event.key === 'd')) {
        event.preventDefault();
        toggleAdminMode();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleKeyCombo);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleKeyCombo);
    };
  }, []);

  const toggleAdminMode = () => {
    const newMode = !isAdminMode;
    setIsAdminMode(newMode);
    localStorage.setItem('mba-admin-mode', newMode.toString());
    
    if (newMode) {
      console.log('%c🔧 Admin Mode Enabled', 'color: #10b981; font-weight: bold; font-size: 16px;');
      console.log('Available commands:', {
        'window.exportLogs()': 'Export application logs',
        'Ctrl+D': 'Toggle debug mode',
        'Triple-click headers': 'Toggle debug mode'
      });
      // Show toast notification
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.success('🔧 Debug mode enabled! Debug buttons are now visible.');
      }
    } else {
      console.log('%c🔧 Admin Mode Disabled', 'color: #ef4444; font-weight: bold; font-size: 16px;');
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.info('Debug mode disabled.');
      }
    }
  };

  // Triple-click handler for emergency admin access
  const handleTripleClick = () => {
    const now = Date.now();
    
    if (now - lastClickTime < 500) {
      setClickCount(prev => prev + 1);
    } else {
      setClickCount(1);
    }
    
    setLastClickTime(now);
    
    if (clickCount === 2) { // This will be the third click
      toggleAdminMode();
      setClickCount(0);
    }
  };

  // Reset click count after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setClickCount(0);
    }, 1000);

    return () => clearTimeout(timer);
  }, [clickCount]);

  return {
    isAdminMode,
    toggleAdminMode,
    handleTripleClick
  };
};

export default useAdminMode; 